/**
 * The permission catalogue — the single source of truth for every
 * `module.resource.action` key in the system (docs/architecture/06-rbac.md).
 * Only code adds a new capability here; admins decide who holds it. This
 * array is synced into the `permissions` table by a seed/migration step —
 * never edited directly in the database.
 */
export const PERMISSION_ACTIONS = [
    'view',
    'create',
    'edit',
    'delete',
    'export',
    'import',
    'assign',
    'approve',
    'manage',
    'configure',
    'convert',
    'merge',
];
export const DATA_SCOPES = ['own', 'team', 'department', 'all'];
function def(module, resource, action, description, scopable = true) {
    return { key: `${module}.${resource}.${action}`, module, resource, action, description, scopable };
}
export const PERMISSIONS = [
    // CRM — leads
    def('crm', 'leads', 'view', 'View leads'),
    def('crm', 'leads', 'create', 'Create leads'),
    def('crm', 'leads', 'edit', 'Edit leads'),
    def('crm', 'leads', 'delete', 'Archive or delete leads'),
    def('crm', 'leads', 'assign', 'Assign or reassign leads'),
    def('crm', 'leads', 'import', 'Import leads or opportunities from CSV, or leads from the API'),
    def('crm', 'leads', 'export', 'Export leads'),
    def('crm', 'leads', 'convert', 'Convert a lead to a contact/company/opportunity'),
    def('crm', 'leads', 'merge', 'Merge duplicate leads'),
    // CRM — contacts
    def('crm', 'contacts', 'view', 'View contacts'),
    def('crm', 'contacts', 'create', 'Create contacts'),
    def('crm', 'contacts', 'edit', 'Edit contacts'),
    def('crm', 'contacts', 'delete', 'Archive or delete contacts'),
    def('crm', 'contacts', 'assign', 'Assign or reassign contacts'),
    def('crm', 'contacts', 'export', 'Export contacts'),
    // CRM — companies
    def('crm', 'companies', 'view', 'View companies'),
    def('crm', 'companies', 'create', 'Create companies'),
    def('crm', 'companies', 'edit', 'Edit companies'),
    def('crm', 'companies', 'delete', 'Archive or delete companies'),
    def('crm', 'companies', 'assign', 'Assign or reassign companies'),
    def('crm', 'companies', 'export', 'Export companies'),
    // CRM — deals (displayed as "Opportunities" in the UI — see RolesPage.tsx's
    // label override; the key stays `deals` so existing stored role grants
    // aren't orphaned)
    def('crm', 'deals', 'view', 'View opportunities'),
    def('crm', 'deals', 'create', 'Create opportunities'),
    def('crm', 'deals', 'edit', 'Edit opportunities, including moving stage'),
    def('crm', 'deals', 'delete', 'Archive or delete opportunities'),
    def('crm', 'deals', 'assign', 'Assign or reassign opportunities'),
    def('crm', 'deals', 'export', 'Export opportunities'),
    // CRM — meetings (Calendly bookings, surfaced on the Calendar page and a
    // lead's Meetings tab)
    def('crm', 'meetings', 'view', 'View scheduled meetings'),
    // Engagement
    def('engagement', 'activities', 'view', 'View activity timelines'),
    def('engagement', 'activities', 'create', 'Log activities'),
    def('engagement', 'activities', 'edit', 'Edit logged activities'),
    def('engagement', 'activities', 'delete', 'Delete logged activities'),
    def('engagement', 'activities', 'export', 'Export the activity feed'),
    def('engagement', 'tasks', 'view', 'View tasks'),
    def('engagement', 'tasks', 'create', 'Create tasks'),
    def('engagement', 'tasks', 'edit', 'Edit tasks'),
    def('engagement', 'tasks', 'delete', 'Delete tasks'),
    def('engagement', 'tasks', 'assign', 'Assign or reassign tasks'),
    def('engagement', 'notes', 'view', 'View notes'),
    def('engagement', 'notes', 'create', 'Create notes'),
    def('engagement', 'notes', 'edit', 'Edit notes'),
    def('engagement', 'notes', 'delete', 'Delete notes'),
    // Reports
    def('reports', 'dashboards', 'view', 'View the dashboard', false),
    def('reports', 'reports', 'view', 'View reports', false),
    def('reports', 'reports', 'export', 'Export reports', false),
    def('reports', 'reports', 'configure', 'Configure dashboard widgets', false),
    // Admin
    def('admin', 'users', 'view', 'View users', false),
    def('admin', 'users', 'export', 'Export the user directory', false),
    def('admin', 'users', 'manage', 'Invite, edit and deactivate users', false),
    def('admin', 'teams', 'manage', 'Manage teams', false),
    def('admin', 'roles', 'manage', 'Manage roles', false),
    def('admin', 'roles', 'configure', 'Configure permission grants on roles', false),
    def('admin', 'settings', 'manage', 'Manage general and company settings', false),
    def('admin', 'branding', 'manage', 'Manage branding', false),
    def('admin', 'auth', 'configure', 'Configure sign-in methods and Entra SSO', false),
    def('admin', 'security', 'configure', 'Configure the security policy', false),
    def('admin', 'custom_fields', 'configure', 'Configure custom fields', false),
    def('admin', 'tags', 'manage', 'Create, edit and delete tags', false),
    def('admin', 'lead_config', 'configure', 'Configure lead statuses, sources and rules', false),
    def('admin', 'pipeline_config', 'configure', 'Configure pipelines and stages', false),
    def('admin', 'email', 'configure', 'Configure the email provider and templates', false),
    def('admin', 'notifications', 'configure', 'Configure notification rules', false),
    def('admin', 'api_keys', 'manage', 'Manage API keys', false),
    def('admin', 'web_forms', 'manage', 'Create and manage public lead-capture forms', false),
    def('admin', 'webhooks', 'manage', 'Manage webhooks', false),
    def('admin', 'integrations', 'manage', 'Manage integrations', false),
    def('admin', 'import_export', 'manage', 'Run and review imports/exports', false),
    def('admin', 'data_retention', 'configure', 'Configure data retention policy', false),
    def('admin', 'ai_qualification', 'configure', 'Configure AI lead qualification', false),
    def('admin', 'whatsapp', 'configure', 'Configure the WhatsApp Business integration', false),
    // AI lead qualification — the review queue, separate from the admin
    // config page above (an analyst/manager reviewing qualified leads
    // doesn't need to be able to reconfigure the AI provider/thresholds).
    def('ai', 'review_queue', 'view', 'View the AI lead review queue'),
    def('ai', 'review_queue', 'manage', 'Approve, reject or reclassify AI-qualified leads'),
    // WhatsApp Inbox — same view/manage split as the AI review queue above.
    def('whatsapp', 'conversations', 'view', 'View WhatsApp conversations'),
    def('whatsapp', 'conversations', 'manage', 'Reply to, assign, and close WhatsApp conversations'),
    // Audit
    def('audit', 'logs', 'view', 'View the audit log', false),
    def('audit', 'logs', 'export', 'Export the audit log', false),
];
export const PERMISSION_KEYS = PERMISSIONS.map((p) => p.key);
/** Default (seeded) role templates → the permission grants they start with.
 * Admins can edit every non-locked role afterwards (docs/architecture/06-rbac.md). */
export const SYSTEM_ROLE_KEYS = [
    'super_admin',
    'admin',
    'manager',
    'sales',
    'user',
    'read_only',
];
//# sourceMappingURL=permissions.js.map